import React, { useState, useEffect } from 'react';
import { useFamily } from '../../contexts/FamilyContext';

/**
 * Sharing Scope selector for content visibility.
 * Displays family checkbox cards with a "Keep this story private" toggle below.
 * For single-family users, shows only the private toggle.
 *
 * Props:
 *   value - current sharing_scope string
 *   onChange - callback(scope, familyIds) when scope changes
 *   isPrivate - whether the story is private
 *   onPrivateChange - callback(boolean) when private toggle changes
 *   className - optional additional CSS classes
 */
function SharingScope({ value = 'single_family', onChange, isPrivate = false, onPrivateChange, className = '' }) {
  const { isMultiFamily, activeFamily, families, activeFamilyId } = useFamily();
  const [checkedFamilyIds, setCheckedFamilyIds] = useState([]);

  // Initialize checked state based on current value
  useEffect(() => {
    if (isPrivate) {
      setCheckedFamilyIds([]);
    } else if (value === 'all_families') {
      setCheckedFamilyIds(families.map(f => f.id));
    } else if (value === 'single_family' && activeFamilyId) {
      setCheckedFamilyIds([activeFamilyId]);
    } else if (value === 'selected_families') {
      // Keep existing checked state
    } else {
      setCheckedFamilyIds([]);
    }
  }, [value, families, activeFamilyId, isPrivate]);

  const toggleFamily = (familyId) => {
    if (isPrivate) return;

    const updated = checkedFamilyIds.includes(familyId)
      ? checkedFamilyIds.filter(id => id !== familyId)
      : [...checkedFamilyIds, familyId];

    setCheckedFamilyIds(updated);

    // Derive scope from checked count
    if (updated.length === 0) {
      onChange('private', []);
    } else if (updated.length === 1 && updated[0] === activeFamilyId) {
      onChange('single_family', updated);
    } else if (updated.length === families.length) {
      onChange('all_families', updated);
    } else {
      onChange('selected_families', updated);
    }
  };

  const handlePrivateToggle = (checked) => {
    if (onPrivateChange) onPrivateChange(checked);
    if (checked) {
      setCheckedFamilyIds([]);
      onChange('private', []);
    } else if (activeFamilyId) {
      setCheckedFamilyIds([activeFamilyId]);
      onChange('single_family', [activeFamilyId]);
    }
  };

  return (
    <div className={`bg-[#F0F4E8] border border-[#6B8E23] rounded-xl p-5 ${className}`}>
      {/* Section header */}
      <div className="mb-4">
        <h4 className="text-[15px] font-bold text-[#556B2F] flex items-center gap-2">
          <span>🏠</span> Share with Families
        </h4>
        <p className="text-[12px] text-[#7A8B5A] mt-0.5">
          Choose who can see this story
        </p>
      </div>

      {/* Family checkbox cards - only for multi-family users */}
      {isMultiFamily && (
        <div className={`space-y-2 transition-opacity ${isPrivate ? 'opacity-40 pointer-events-none' : ''}`}>
          {families.map((family) => {
            const isChecked = checkedFamilyIds.includes(family.id);
            return (
              <button
                key={family.id}
                type="button"
                onClick={() => toggleFamily(family.id)}
                className={`w-full flex items-center gap-3 h-[52px] px-4 rounded-lg transition-all ${
                  isChecked
                    ? 'border-[#6B8E23] border-2 bg-white'
                    : 'border-gray-300 border bg-white'
                }`}
              >
                {/* Checkbox */}
                <div
                  className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 transition-colors ${
                    isChecked ? 'bg-[#6B8E23]' : 'border-2 border-gray-300 bg-white'
                  }`}
                >
                  {isChecked && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M5 13l4 4L19 7" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>

                {/* Family info */}
                <div className="flex-1 text-left min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[13px]">🏠</span>
                    <span className="text-[13px] font-medium text-gray-800 truncate">
                      {family.name}
                    </span>
                  </div>
                  {family.member_count != null && (
                    <p className="text-[11px] text-gray-400 mt-0.5">
                      {family.member_count} {family.member_count === 1 ? 'member' : 'members'}
                    </p>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Private toggle - below families */}
      <button
        type="button"
        onClick={() => handlePrivateToggle(!isPrivate)}
        className={`w-full flex items-center gap-3 h-[52px] px-4 rounded-lg transition-all mt-3 ${
          isPrivate
            ? 'border-red-500 border-2 bg-red-50'
            : 'border-gray-300 border bg-white'
        }`}
      >
        {/* Checkbox */}
        <div
          className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 transition-colors ${
            isPrivate ? 'bg-red-500' : 'border-2 border-gray-300 bg-white'
          }`}
        >
          {isPrivate && (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M5 13l4 4L19 7" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </div>

        {/* Label */}
        <div className="flex-1 text-left">
          <span className={`text-[13px] font-medium ${isPrivate ? 'text-red-600' : 'text-gray-800'}`}>
            🔒 Keep this story private
          </span>
          <p className={`text-[11px] mt-0.5 ${isPrivate ? 'text-red-400' : 'text-gray-400'}`}>
            Only you can see this story
          </p>
        </div>
      </button>

      {/* Privacy tip */}
      <p className="text-[11px] text-[#7A8B5A] mt-4 leading-relaxed">
        <span className="font-semibold">Tip:</span> Stories are yours. You control which families see them. You can always change sharing settings later.
      </p>
    </div>
  );
}

export default SharingScope;
